import {
    Children,
    ComponentProps,
    createContext,
    isValidElement,
    ReactElement,
    ReactNode,
    useContext,
} from 'react';
import styled, { useTheme } from 'styled-components';
import { Row, Col, Switch, Slider, Flex } from 'antd';
import { SliderRangeProps } from 'antd/lib/slider';
import isNil from 'lodash/isNil';

export const MIN_DAILY_SCHEDULE_HOURS = 6;
export const MIN_ENABLED_SENDING_DAYS = 5;
export const MAX_ENABLED_SENDING_DAYS = 5;
const StyledCol = styled(Col)`
    ${(props) => {
        const prefix = props.theme['@ant-prefix'];
        return `
		.${prefix}-tooltip {
			z-index: 0;
		}
		.${prefix}-tooltip-inner {
			background-color: white;
			color: black;
			box-shadow: none;
			white-space: nowrap;
		}
	`;
    }}
`;

export const timeFormatter = (dayMinutes: number): string => {
    const hours = Math.floor(dayMinutes / 60) % 12;
    const minutes = dayMinutes % 60;
    const unit = dayMinutes >= 720 && dayMinutes < 1440 ? 'p.m.' : 'a.m.';
    return `${hours === 0 ? 12 : hours < 10 ? hours : hours}:${
        minutes < 10 ? `0${minutes}` : minutes
    } ${unit}`;
};

interface LabelledRangeSliderProps extends Omit<SliderRangeProps, 'range'> {
    label: ReactNode;
    isEnabled: boolean;
    onEnableToggle: (state: boolean) => unknown;
    validator?: (value: number[]) => string | undefined;
}

/**
 * Range selector with enable toggle and label to the left
 * @returns
 */
const LabelledRangeSlider: FCWithChildren<LabelledRangeSliderProps> = (props) => {
    const { label, isEnabled, onEnableToggle, validator, ...sliderProps } = props;
    const { value } = sliderProps;

    const error = validator?.(value ?? [0, 0]);
    const theme = useTheme();
    return (
        <>
            <Row align={'middle'} gutter={theme['@spacer-num-sm']}>
                <Col>
                    <Flex justify="space-between" align="center" style={{ minWidth: '90px' }}>
                        {label}
                        <Switch checked={isEnabled} onChange={onEnableToggle} />
                    </Flex>
                </Col>

                <StyledCol flex="auto">
                    {isEnabled ? (
                        <Slider
                            min={0}
                            max={1440}
                            step={30}
                            tooltip={{
                                open: true,
                                formatter: (time?: number) => (time ? timeFormatter(time) : time),
                                getPopupContainer: (node) => node.parentElement ?? node,
                            }}
                            range
                            allowCross={false}
                            {...sliderProps}
                        />
                    ) : (
                        <></>
                    )}
                </StyledCol>
            </Row>
            <Row style={{ minHeight: '2em', padding: 0, margin: 0 }}>
                {isEnabled && error ? (
                    <Col offset={5}>
                        <h4 style={{ color: 'red' }}>{error}</h4>
                    </Col>
                ) : null}
            </Row>
        </>
    );
};

interface ISyncedRangeSliderContext {
    syncedOnChange: (value: number[]) => unknown;
    isSynced: boolean;
}
/**
 * Context for synced range slider
 */
const SyncedRangeSliderContext = createContext<ISyncedRangeSliderContext>({
    syncedOnChange: () => {},
    isSynced: false,
});

type RangeSlidersProps = {
    /**
     * Whether the child sliders should be synced to same value or not
     */
    isSynced: boolean;
    /**
     * Callback to be called if sync is enabled and value changes. If not provided, the value will be passed to all child sliders' onChange callbacks.
     */
    onChangeSynced?: (value: number[]) => void;
};

type RangeSliderComponent = FCWithChildren<RangeSlidersProps> & {
    Slider: typeof SyncedRangeSlider;
};

/**
 * Container component for a a group of ranged sliders that can be in sync
 */
const RangeSliders: RangeSliderComponent = ({ children, isSynced, onChangeSynced }) => {
    const onChangeCallbacks =
        Children.map(children, (child) =>
            isSyncedRangeSlider(child) ? child.props.onChange : undefined
        )?.filter((callback) => !!callback) ?? [];

    const syncedOnChange = (value: number[]) => {
        !isNil(onChangeSynced)
            ? onChangeSynced(value)
            : onChangeCallbacks.forEach((callback) => callback(value));
    };

    return (
        <SyncedRangeSliderContext.Provider value={{ syncedOnChange, isSynced }}>
            {Children.map(children, (child) => (
                <div style={{ marginTop: '2em' }}>{child}</div>
            ))}
        </SyncedRangeSliderContext.Provider>
    );
};

const withSync = (
    Component: typeof LabelledRangeSlider
): FCWithChildren<LabelledRangeSliderProps> => {
    return (props) => {
        const { syncedOnChange, isSynced } =
            useContext<ISyncedRangeSliderContext>(SyncedRangeSliderContext);
        const { onChange: originalOnChange, ...componentProps } = props;
        const onChange = isSynced ? syncedOnChange : originalOnChange;
        return <Component {...componentProps} onChange={onChange} />;
    };
};

const SyncedRangeSlider = withSync(LabelledRangeSlider);

const isSyncedRangeSlider = (
    element: ReactNode
): element is ReactElement<ComponentProps<typeof SyncedRangeSlider>> =>
    isValidElement(element) && element.type === SyncedRangeSlider;

RangeSliders.Slider = SyncedRangeSlider;

export default RangeSliders;
